/**
 * SL-0045 Activity 10 (anomaly 4): the client-side workspace-domain predicate behind hiding
 * `identity-documents` from accounts that are not on the Google Workspace domain.
 *
 * **This closes a discoverability leak, not a confidentiality hole.** Server-side enforcement
 * already exists and remains authoritative: `WorkspaceDomainGuard`
 * (`platform/identity-documents/src/identity-document/interface/workspace-domain.guard.ts`) is
 * applied at `transcribe-document.controller.ts`, so a non-workspace caller reaching the API
 * directly is refused there regardless of what the console renders.
 *
 * **The deliberate duplication, recorded rather than re-derived (Task 10.1, decided 2026-08-08).**
 * The server's own domain source is the exported `WORKSPACE_EMAIL_DOMAIN` constant in
 * `verify-workspace-member.use-case.ts`, and it is NOT reachable from `products/**` without
 * violating the module seam. This side therefore reads the client's own configured source,
 * `VITE_GOOGLE_WORKSPACE_HOSTED_DOMAIN` — the same variable the Google `hd` pin uses in
 * `AuthProvider.tsx`, set to `dgbiotech.eu` in `.env`, `.env.staging` and `.env.production`. One
 * domain, two configured sources, one per side of the seam.
 *
 * Read at CALL time rather than module-load time: `.env.test` deliberately does not define the
 * variable, so a test that needs a workspace identity stubs it per case instead of the module
 * capturing an undefined value once at import.
 */
export function isWorkspaceEmail(email: string | null | undefined): boolean {
  const configuredDomain = import.meta.env.VITE_GOOGLE_WORKSPACE_HOSTED_DOMAIN;
  // Fail closed on an unconfigured domain: the cost is a hidden nav entry on a surface the server
  // guards anyway, whereas the mirror default would show a workspace-only module to everyone the
  // moment the variable goes missing.
  if (typeof configuredDomain !== 'string' || configuredDomain.length === 0) {
    return false;
  }
  if (typeof email !== 'string') {
    return false;
  }
  const separator = email.lastIndexOf('@');
  if (separator < 0) {
    return false;
  }
  return email.slice(separator + 1).toLowerCase() === configuredDomain.toLowerCase();
}
